import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ArrowLeft, Star, AlertTriangle, Check, Users, Copy, X } from 'lucide-react';
import {
  getSsgSession, updateSsgSession, getSsgSessionPoints, putSsgSessionPoints, publishSsgSession,
  getSsgPeriods, getSsgSessions, getSsgLeaderboard, getPlayers,
} from '../../api';
import { SSG_RESULTS, ssgResultMeta } from '../../ssgConstants';

const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px' };
const btn = (active, color = '#3b82f6') => ({
  padding: '7px 14px', borderRadius: 8, border: `1px solid ${active ? color : 'var(--border)'}`,
  background: active ? color : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
  fontSize: 12.5, fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit',
});
const inputStyle = {
  padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)',
  background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 12.5,
  fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
};

// Position groups sorted in a standard back-to-front reading order — the default row sort.
// Anything not in this list (shouldn't happen, but active players can have odd/blank
// positions) sorts to the end rather than throwing.
const POSITION_GROUP_ORDER = ['GK', 'CB', 'FB', 'CDM', 'CAM', 'W', 'ST'];
function positionRank(pos) {
  const i = POSITION_GROUP_ORDER.indexOf(pos);
  return i === -1 ? POSITION_GROUP_ORDER.length : i;
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtClock(d) {
  return new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

// Bulk "activate" helpers (All present / Copy attendance) have no source result to copy — the
// schema has no "present but unscored" state, only NULL/0/1/3 — so both default a newly-
// activated row to Draw(1): a neutral, visually-distinct (amber) placeholder the person entering
// is expected to correct via 0/1/3 or "Select winners", not a claim about what actually happened.
const DEFAULT_ACTIVATED_BASE = 1;

export default function SsgSessionEditor({ sessionId, user, onBack }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState([]); // editable working copy
  const [leaderboardByPlayer, setLeaderboardByPlayer] = useState({});
  const [prevResultByPlayer, setPrevResultByPlayer] = useState({});
  const [sortMode, setSortMode] = useState('position'); // position | alpha | rank
  const [selectWinnersMode, setSelectWinnersMode] = useState(false);
  const [winnerSelection, setWinnerSelection] = useState(() => new Set());
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const rowRefs = useRef([]);
  const bonusInputRefs = useRef([]);
  const blurSaveTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sess, players, periods, pointsRows] = await Promise.all([
        getSsgSession(sessionId), getPlayers(), getSsgPeriods(), getSsgSessionPoints(sessionId),
      ]);
      setSession(sess);
      setNotes(sess.notes || '');

      const period = periods.find(p => p.season === sess.season && sess.matchday >= p.start_matchday && sess.matchday <= p.end_matchday);

      const activePlayers = (players || []).filter(p => p.active);
      const pointsByPlayer = {};
      (pointsRows || []).forEach(r => { pointsByPlayer[r.player_id] = r; });
      setRows(activePlayers.map(p => {
        const existing = pointsByPlayer[p.id] || {};
        return {
          player_id: p.id, name: p.name, position: p.position, squad_number: p.squad_number, photo_url: p.photo_url,
          base_points: existing.base_points ?? null,
          bonus_points: existing.bonus_points ?? 0,
          bonus_reason: existing.bonus_reason || '',
          is_mvp: existing.is_mvp || false,
          note: existing.note || '',
        };
      }));
      setDirty(false);

      if (period) {
        const [lb, sessionsInPeriod] = await Promise.all([
          getSsgLeaderboard(period.id, 'period'),
          getSsgSessions({ period_id: period.id, requesterRole: 'staff' }),
        ]);
        const lbMap = {};
        (lb || []).forEach(r => { lbMap[r.player_id] = r; });
        setLeaderboardByPlayer(lbMap);

        const prior = (sessionsInPeriod || [])
          .filter(s => s.status === 'published' && s.id !== Number(sessionId) &&
            (new Date(s.session_date) < new Date(sess.session_date) ||
             (new Date(s.session_date).getTime() === new Date(sess.session_date).getTime() && s.id < Number(sessionId))))
          .sort((a, b) => new Date(b.session_date) - new Date(a.session_date) || b.id - a.id)[0];

        if (prior) {
          const priorPoints = await getSsgSessionPoints(prior.id);
          const prevMap = {};
          (priorPoints || []).forEach(r => { prevMap[r.player_id] = r.base_points; });
          setPrevResultByPlayer(prevMap);
        } else {
          setPrevResultByPlayer({});
        }
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  // ── Editing helpers ─────────────────────────────────────────────────────────
  const updateRow = (playerId, patch) => {
    setRows(prev => prev.map(r => r.player_id === playerId ? { ...r, ...patch } : r));
    setDirty(true);
  };

  const setResult = (playerId, value) => updateRow(playerId, { base_points: value });
  const toggleAbsent = (playerId) => {
    setRows(prev => prev.map(r => r.player_id === playerId
      ? { ...r, base_points: r.base_points == null ? DEFAULT_ACTIVATED_BASE : null, bonus_points: r.base_points == null ? r.bonus_points : 0, bonus_reason: r.base_points == null ? r.bonus_reason : '', is_mvp: r.base_points == null ? r.is_mvp : false }
      : r));
    setDirty(true);
  };
  const setMvp = (playerId) => {
    setRows(prev => prev.map(r => ({ ...r, is_mvp: r.player_id === playerId })));
    setDirty(true);
  };

  // ── Bulk helpers ────────────────────────────────────────────────────────────
  const handleAllPresent = () => {
    setRows(prev => prev.map(r => r.base_points == null ? { ...r, base_points: DEFAULT_ACTIVATED_BASE } : r));
    setDirty(true);
  };
  const handleClearAll = () => {
    setRows(prev => prev.map(r => ({ ...r, base_points: null, bonus_points: 0, bonus_reason: '', is_mvp: false })));
    setDirty(true);
  };
  const handleCopyAttendance = () => {
    setRows(prev => prev.map(r => {
      if (r.base_points != null) return r; // never overwrite an already-active row
      const prevBase = prevResultByPlayer[r.player_id];
      return prevBase != null ? { ...r, base_points: DEFAULT_ACTIVATED_BASE } : r;
    }));
    setDirty(true);
  };

  const applyWinners = () => {
    setRows(prev => prev.map(r => {
      if (r.base_points == null) return r; // never touches absent players
      return { ...r, base_points: winnerSelection.has(r.player_id) ? 3 : 0 };
    }));
    setDirty(true);
    setSelectWinnersMode(false);
    setWinnerSelection(new Set());
  };

  // ── Save / autosave ─────────────────────────────────────────────────────────
  const saveDraft = useCallback(async (silent = true) => {
    if (!silent) setSaving(true);
    try {
      const payload = rows.map(r => ({
        player_id: r.player_id,
        base_points: r.base_points,
        bonus_points: r.base_points == null ? 0 : (r.bonus_points || 0),
        bonus_reason: r.base_points == null ? null : (r.bonus_reason || null),
        is_mvp: r.base_points == null ? false : r.is_mvp,
        note: r.note || null,
      }));
      await putSsgSessionPoints(sessionId, payload, 'staff');
      if (notes !== (session?.notes || '')) {
        await updateSsgSession(sessionId, { notes }, 'staff');
      }
      setDirty(false);
      setLastSavedAt(new Date());
    } finally {
      if (!silent) setSaving(false);
    }
  }, [rows, notes, session, sessionId]);

  useEffect(() => {
    const interval = setInterval(() => { if (dirty && !saving) saveDraft(true); }, 20000);
    return () => clearInterval(interval);
  }, [dirty, saving, saveDraft]);

  const handleRowBlur = () => {
    clearTimeout(blurSaveTimer.current);
    blurSaveTimer.current = setTimeout(() => { if (dirty && !saving) saveDraft(true); }, 600);
  };
  useEffect(() => () => clearTimeout(blurSaveTimer.current), []);

  // ── Keyboard navigation ──────────────────────────────────────────────────────
  const focusRow = (idx) => {
    const el = rowRefs.current[idx];
    if (el) el.focus();
  };

  const handleRowKeyDown = (e, idx, playerId) => {
    const onRowItself = e.target === e.currentTarget;
    if (e.key === 'ArrowDown' && onRowItself) { e.preventDefault(); focusRow(Math.min(idx + 1, sortedRows.length - 1)); return; }
    if (e.key === 'ArrowUp' && onRowItself) { e.preventDefault(); focusRow(Math.max(idx - 1, 0)); return; }
    if (e.key === 'Enter') { e.preventDefault(); focusRow(Math.min(idx + 1, sortedRows.length - 1)); return; }
    if (!onRowItself) return; // everything below only fires with the row div itself focused
    if (e.key === '0' || e.key === '1' || e.key === '3') { e.preventDefault(); setResult(playerId, Number(e.key)); return; }
    if (e.key === 'a' || e.key === 'A') { e.preventDefault(); toggleAbsent(playerId); return; }
    if (e.key === 'm' || e.key === 'M') { e.preventDefault(); setMvp(playerId); return; }
    if (e.key === 'b' || e.key === 'B') {
      e.preventDefault();
      const bonusEl = bonusInputRefs.current[idx];
      if (bonusEl) bonusEl.focus();
    }
  };

  // ── Sort ────────────────────────────────────────────────────────────────────
  const sortedRows = useMemo(() => {
    const copy = [...rows];
    if (sortMode === 'alpha') copy.sort((a, b) => a.name.localeCompare(b.name));
    else if (sortMode === 'rank') {
      copy.sort((a, b) => (leaderboardByPlayer[a.player_id]?.rank ?? 999) - (leaderboardByPlayer[b.player_id]?.rank ?? 999) || a.name.localeCompare(b.name));
    } else {
      copy.sort((a, b) => positionRank(a.position) - positionRank(b.position) || (a.squad_number ?? 999) - (b.squad_number ?? 999) || a.name.localeCompare(b.name));
    }
    return copy;
  }, [rows, sortMode, leaderboardByPlayer]);

  // ── Footer totals ────────────────────────────────────────────────────────────
  const scoredRows = rows.filter(r => r.base_points != null);
  const totalBase = scoredRows.reduce((s, r) => s + r.base_points, 0);
  const totalBonus = scoredRows.reduce((s, r) => s + (r.bonus_points || 0), 0);

  // ── Publish preview (client-side approximation — see comment on standingsPreview) ──────────
  // Not a server-verified "dry run" (the leaderboard endpoint only counts published sessions,
  // and this session isn't published yet at preview time) — this adds each player's pending
  // total for THIS session on top of their already-published total and re-sorts on points alone,
  // skipping the full W/D/L/avg tie-break chain the real leaderboard uses. Good enough for a
  // heads-up before publishing; the real numbers land the moment publish actually happens.
  const standingsPreview = useMemo(() => {
    const merged = Object.values(leaderboardByPlayer).map(lb => {
      const row = rows.find(r => r.player_id === lb.player_id);
      const pending = row && row.base_points != null ? row.base_points + (row.bonus_points || 0) : 0;
      return { ...lb, newTotal: lb.total_points + pending };
    });
    merged.sort((a, b) => b.newTotal - a.newTotal);
    merged.forEach((r, i) => { r.newRank = i + 1; });
    return merged;
  }, [leaderboardByPlayer, rows]);

  const publishWarnings = useMemo(() => {
    const warnings = [];
    if (scoredRows.length < 8) warnings.push(`Only ${scoredRows.length} player${scoredRows.length === 1 ? '' : 's'} marked present.`);
    if (!rows.some(r => r.is_mvp)) warnings.push('No MVP set.');
    const distinctResults = new Set(scoredRows.map(r => r.base_points));
    if (scoredRows.length > 0 && distinctResults.size === 1) warnings.push('Every present player has the same result.');
    return warnings;
  }, [rows, scoredRows]);

  // Unlike publishWarnings above (advisory, shown but never stop publishing), a bonus of 3+
  // with no reason is a hard requirement ("Require a bonus reason when bonus_points >= 3"),
  // not a nudge — publish is blocked until every such row has one.
  const missingBonusReasons = useMemo(
    () => rows.filter(r => (r.bonus_points || 0) >= 3 && !r.bonus_reason?.trim()),
    [rows]
  );

  const handleOpenPublishConfirm = async () => {
    if (dirty) await saveDraft(true);
    setShowPublishConfirm(true);
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await saveDraft(true);
      await publishSsgSession(sessionId, 'staff');
      setShowPublishConfirm(false);
      await load();
    } finally {
      setPublishing(false);
    }
  };

  if (loading || !session) {
    return <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-dimmed)' }}>Loading session…</div>;
  }

  const wasPublished = session.status === 'published';

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 90 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <button onClick={onBack} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12.5, marginBottom: 8, padding: 0 }}>
            <ArrowLeft size={14} /> Back to sessions
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h1 style={{ fontSize: 20, fontWeight: 700 }}>{fmtDate(session.session_date)} — MD{session.matchday}</h1>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99,
              background: wasPublished ? '#10b98122' : 'var(--border)',
              color: wasPublished ? '#10b981' : 'var(--text-muted)',
            }}>
              {wasPublished ? 'Published' : 'Draft'}
            </span>
            {session.format && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{session.format}</span>}
          </div>
          {wasPublished && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 12, color: '#f59e0b' }}>
              <AlertTriangle size={13} /> This session is published — saving changes here will change the standings.
            </div>
          )}
        </div>
        <div style={{ minWidth: 260, flex: '0 1 360px' }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4, display: 'block' }}>Session notes</label>
          <input
            style={inputStyle}
            placeholder='e.g. "3 teams of 5, 8-minute games"'
            value={notes}
            onChange={e => { setNotes(e.target.value); setDirty(true); }}
            onBlur={handleRowBlur}
          />
        </div>
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {[['position', 'Position'], ['alpha', 'A–Z'], ['rank', 'Rank']].map(([id, label]) => (
            <button key={id} style={btn(sortMode === id)} onClick={() => setSortMode(id)}>{label}</button>
          ))}
        </div>
        <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border-strong)', margin: '0 4px' }} />
        <button style={btn(false)} onClick={handleAllPresent}>All present</button>
        <button style={btn(false)} onClick={handleClearAll}>Clear all</button>
        <button style={btn(false)} onClick={handleCopyAttendance} disabled={!Object.keys(prevResultByPlayer).length} title="Copies who attended last session, not their results">
          <Copy size={12} style={{ verticalAlign: -1, marginRight: 4 }} />Copy attendance from previous session
        </button>
        <button style={btn(selectWinnersMode, '#10b981')} onClick={() => { setSelectWinnersMode(s => !s); setWinnerSelection(new Set()); }}>
          <Users size={12} style={{ verticalAlign: -1, marginRight: 4 }} />Select winners
        </button>
        {selectWinnersMode && (
          <>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{winnerSelection.size} selected</span>
            <button style={btn(true, '#10b981')} onClick={applyWinners}>Apply — winners get 3, others get 0</button>
            <button style={btn(false)} onClick={() => { setSelectWinnersMode(false); setWinnerSelection(new Set()); }}>Cancel</button>
          </>
        )}
        <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-dimmed)' }}>
          {saving ? 'Saving…' : lastSavedAt ? `Last saved ${fmtClock(lastSavedAt)}` : 'Not saved yet'}
        </div>
      </div>

      <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 10 }}>
        Keyboard: focus a row, press <b>0/1/3</b> for Loss/Draw/Win, <b>A</b> absent, <b>B</b> jump to bonus, <b>M</b> set MVP, <b>Enter</b> next row.
      </div>

      {/* Table */}
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '28px 34px 1fr 70px 90px 70px 210px 70px 40px 1fr', gap: 8, padding: '8px 12px', fontSize: 10, fontWeight: 700, color: 'var(--text-dimmed)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)' }}>
          <div />
          <div />
          <div>Player</div>
          <div>Rank</div>
          <div>Points</div>
          <div>Prev</div>
          <div>Result</div>
          <div>Bonus</div>
          <div style={{ textAlign: 'center' }}>MVP</div>
          <div>Note</div>
        </div>
        {sortedRows.map((r, idx) => {
          const active = r.base_points != null;
          const meta = ssgResultMeta(r.base_points);
          const lb = leaderboardByPlayer[r.player_id];
          const prevBase = prevResultByPlayer[r.player_id];
          const prevMeta = prevBase !== undefined ? ssgResultMeta(prevBase) : null;
          return (
            <div
              key={r.player_id}
              ref={el => { rowRefs.current[idx] = el; }}
              tabIndex={0}
              onKeyDown={e => handleRowKeyDown(e, idx, r.player_id)}
              onFocus={() => {}}
              onBlur={handleRowBlur}
              style={{
                display: 'grid', gridTemplateColumns: '28px 34px 1fr 70px 90px 70px 210px 70px 40px 1fr', gap: 8,
                alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid var(--border)',
                background: active ? 'transparent' : 'var(--bg-app)', opacity: active ? 1 : 0.55,
                outline: 'none', cursor: 'pointer',
              }}
              className="ssg-row"
              onClick={() => focusRow(idx)}
            >
              {selectWinnersMode ? (
                <input
                  type="checkbox"
                  tabIndex={-1}
                  disabled={!active}
                  checked={winnerSelection.has(r.player_id)}
                  onChange={e => {
                    setWinnerSelection(prev => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(r.player_id); else next.delete(r.player_id);
                      return next;
                    });
                  }}
                  onClick={e => e.stopPropagation()}
                />
              ) : (
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: active ? (meta?.color || '#64748b') : 'var(--border-strong)', margin: '0 auto' }} />
              )}
              {r.photo_url ? (
                <img src={r.photo_url} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
              ) : (
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: 'var(--text-muted)' }}>
                  {r.name.split(' ').map(s => s[0]).slice(0, 2).join('')}
                </div>
              )}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
                <div style={{ fontSize: 10, color: 'var(--text-dimmed)' }}>{r.position}{r.squad_number ? ` · #${r.squad_number}` : ''}</div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {lb ? `${lb.shared_rank ? '=' : ''}${lb.rank}` : '—'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{lb ? `${lb.total_points} pts` : '—'}</div>
              <div>
                {prevMeta ? (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 5, background: prevMeta.color + '22', color: prevMeta.color }}>{prevMeta.short}</span>
                ) : prevBase === null ? (
                  <span style={{ fontSize: 10, color: 'var(--text-dimmed)' }}>Absent</span>
                ) : (
                  <span style={{ fontSize: 10, color: 'var(--text-dimmed)' }}>—</span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} onClick={e => e.stopPropagation()}>
                {SSG_RESULTS.map(res => (
                  <button
                    key={res.value}
                    tabIndex={-1}
                    onClick={() => { focusRow(idx); setResult(r.player_id, res.value); }}
                    style={{
                      flex: 1, padding: '5px 0', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                      border: `1px solid ${active && r.base_points === res.value ? res.color : 'var(--border)'}`,
                      background: active && r.base_points === res.value ? res.color : 'transparent',
                      color: active && r.base_points === res.value ? '#fff' : 'var(--text-secondary)',
                    }}
                  >{res.label}</button>
                ))}
                <button
                  tabIndex={-1}
                  onClick={() => { focusRow(idx); toggleAbsent(r.player_id); }}
                  title="Toggle absent"
                  style={{
                    padding: '5px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: 'pointer',
                    border: '1px solid var(--border)', background: !active ? 'var(--border)' : 'transparent',
                    color: 'var(--text-muted)',
                  }}
                >ABS</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }} onClick={e => e.stopPropagation()}>
                <input
                  ref={el => { bonusInputRefs.current[idx] = el; }}
                  type="number"
                  disabled={!active}
                  tabIndex={0}
                  value={r.bonus_points || ''}
                  placeholder="0"
                  onChange={e => updateRow(r.player_id, { bonus_points: e.target.value === '' ? 0 : Number(e.target.value) })}
                  onBlur={handleRowBlur}
                  style={{ ...inputStyle, padding: '3px 6px', fontSize: 11 }}
                />
                {r.bonus_points > 0 && (
                  <input
                    disabled={!active}
                    tabIndex={0}
                    value={r.bonus_reason}
                    placeholder={r.bonus_points >= 3 ? 'Reason (required)' : 'Reason'}
                    onChange={e => updateRow(r.player_id, { bonus_reason: e.target.value })}
                    onBlur={handleRowBlur}
                    style={{
                      ...inputStyle, padding: '3px 6px', fontSize: 11,
                      borderColor: r.bonus_points >= 3 && !r.bonus_reason ? '#ef4444' : 'var(--border)',
                    }}
                  />
                )}
                {r.bonus_points > 5 && (
                  <div style={{ fontSize: 9, color: '#f59e0b' }}>Large bonus — double-check</div>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'center' }} onClick={e => e.stopPropagation()}>
                <button
                  tabIndex={-1}
                  onClick={() => { focusRow(idx); if (active) setMvp(r.player_id); }}
                  disabled={!active}
                  title="MVP"
                  style={{ background: 'none', border: 'none', cursor: active ? 'pointer' : 'default', padding: 2 }}
                >
                  <Star size={16} fill={r.is_mvp ? '#f59e0b' : 'none'} color={r.is_mvp ? '#f59e0b' : 'var(--border-strong)'} />
                </button>
              </div>
              <input
                tabIndex={0}
                value={r.note}
                placeholder="—"
                onChange={e => updateRow(r.player_id, { note: e.target.value })}
                onBlur={handleRowBlur}
                onClick={e => e.stopPropagation()}
                style={{ ...inputStyle, fontSize: 11 }}
              />
            </div>
          );
        })}
      </div>

      {/* Sticky footer */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, background: 'var(--bg-card)', borderTop: '1px solid var(--border-strong)',
        padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 24, zIndex: 50, boxShadow: '0 -4px 16px rgba(0,0,0,0.15)',
      }}>
        <div style={{ fontSize: 12.5 }}><b>{scoredRows.length}</b> scored</div>
        <div style={{ fontSize: 12.5 }}><b>{totalBase}</b> base points</div>
        <div style={{ fontSize: 12.5 }}><b>{totalBonus}</b> bonus points</div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
          <button style={btn(false)} onClick={() => saveDraft(false)} disabled={saving}>
            {saving ? 'Saving…' : 'Save draft'}
          </button>
          <button style={btn(true, '#10b981')} onClick={handleOpenPublishConfirm}>
            {wasPublished ? 'Re-publish' : 'Publish'}
          </button>
        </div>
      </div>

      {/* Publish confirm modal */}
      {showPublishConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 16, width: '100%', maxWidth: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{wasPublished ? 'Re-publish session' : 'Publish session'}</h3>
              <button onClick={() => setShowPublishConfirm(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <div style={{ padding: 20 }}>
              {wasPublished && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: 10, borderRadius: 8, background: '#f59e0b1a', color: '#f59e0b', fontSize: 12.5, marginBottom: 14 }}>
                  <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                  This session was already published — re-saving will change the standings.
                </div>
              )}
              {missingBonusReasons.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '6px 10px', borderRadius: 8, background: '#ef444433', border: '1px solid #ef4444', color: '#ef4444', fontSize: 12.5, marginBottom: 6, fontWeight: 600 }}>
                    <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                    Bonus reason required for: {missingBonusReasons.map(r => r.name).join(', ')}
                  </div>
                </div>
              )}
              {publishWarnings.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  {publishWarnings.map(w => (
                    <div key={w} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '6px 10px', borderRadius: 8, background: '#ef44441a', color: '#ef4444', fontSize: 12.5, marginBottom: 6 }}>
                      <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} /> {w}
                    </div>
                  ))}
                </div>
              )}
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                New top 5 (estimated)
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
                {standingsPreview.slice(0, 5).map(p => {
                  const delta = p.rank - p.newRank;
                  return (
                    <div key={p.player_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '4px 8px', borderRadius: 6, background: 'var(--bg-app)' }}>
                      <span style={{ width: 20, fontWeight: 700 }}>{p.newRank}</span>
                      <span style={{ flex: 1 }}>{p.name}</span>
                      <span style={{ color: 'var(--text-muted)' }}>{p.newTotal} pts</span>
                      {delta !== 0 && (
                        <span style={{ fontSize: 11, fontWeight: 700, color: delta > 0 ? '#10b981' : '#ef4444' }}>
                          {delta > 0 ? `▲${delta}` : `▼${Math.abs(delta)}`}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button style={btn(false)} onClick={() => setShowPublishConfirm(false)}>Cancel</button>
                <button style={btn(true, '#10b981')} onClick={handlePublish} disabled={publishing || missingBonusReasons.length > 0}>
                  <Check size={13} style={{ verticalAlign: -2, marginRight: 4 }} />
                  {publishing ? 'Publishing…' : 'Confirm & publish'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
